export interface InternationalTraffic {
  language: 'en' | 'es' | 'pt' | 'fr';
  country: string;
  traffic: number;
  percentage: number;
  keywords: number;
  avgPosition: number;
}

export interface BrandMetrics {
  branded: {
    keywords: number;
    traffic: number;
    percentage: number;
  };
  nonBranded: {
    keywords: number;
    traffic: number;
    percentage: number;
  };
}

export interface SearchIntent {
  type: 'informational' | 'navigational' | 'transactional' | 'commercial';
  volume: number;
  percentage: number;
  keywords: number;
  traffic: number;
  examples: string[];
}

export interface InternationalPage {
  url: string;
  language: 'en' | 'es' | 'pt' | 'fr';
  traffic: number;
  keywords: number;
  mainKeyword: string;
  conversionRate: number;
}

export interface InternationalKeyword {
  id: string;
  language: 'en' | 'es' | 'pt';
  keyword: string;
  volume: number;
  position: number;
  intent: 'informational' | 'navigational' | 'transactional' | 'commercial';
  targetUrl: string;
  opportunity: 'high' | 'medium' | 'low';
}

// Données réelles SEMrush pour Le 39V
export const international39V = {
  traffic: {
    fr: {
      language: 'fr',
      country: 'France',
      traffic: 15, // 69.24% de 21
      percentage: 69.24,
      keywords: 34,
      avgPosition: 1.5
    },
    us: {
      language: 'en',
      country: 'États-Unis',
      traffic: 1.5, // 7% de 21
      percentage: 7.00,
      keywords: 3,
      avgPosition: 4.0
    },
    nl: {
      language: 'en',
      country: 'Pays-Bas',
      traffic: 0.6, // 2.87% de 21
      percentage: 2.87,
      keywords: 1,
      avgPosition: 8.0
    },
    uk: {
      language: 'en',
      country: 'Royaume-Uni',
      traffic: 0.6, // 2.87% de 21
      percentage: 2.87,
      keywords: 1,
      avgPosition: 6.0
    }
  } as Record<string, InternationalTraffic>,

  // Données réelles SEMrush - 100% trafic de marque
  brandMetrics: {
    branded: {
      keywords: 39, // Tous les mots-clés sont de marque
      traffic: 21,
      percentage: 100.0
    },
    nonBranded: {
      keywords: 0,
      traffic: 0,
      percentage: 0.0
    }
  } as BrandMetrics,

  // Données réelles SEMrush
  searchIntents: [
    {
      type: 'informational',
      volume: 140,
      percentage: 6.3,
      keywords: 3,
      traffic: 1.33,
      examples: ['restaurant gastronomique paris', 'fine dining paris']
    },
    {
      type: 'navigational',
      volume: 3600,
      percentage: 81.3,
      keywords: 39,
      traffic: 17.07,
      examples: ['le 39v', 'le 39 v', '39v']
    },
    {
      type: 'commercial',
      volume: 80,
      percentage: 3.8,
      keywords: 2,
      traffic: 0.80,
      examples: ['restaurant 8ème paris']
    },
    {
      type: 'transactional',
      volume: 180,
      percentage: 8.8,
      keywords: 4,
      traffic: 1.85,
      examples: ['réserver le 39v', 'booking le 39v']
    }
  ] as SearchIntent[],

  // Pages principales (données réelles SEMrush)
  topPages: [
    {
      url: 'https://www.le39v.com/en',
      language: 'en',
      traffic: 20.7, // 98.6% du trafic
      keywords: 39,
      mainKeyword: 'le 39v',
      conversionRate: 8.5
    },
    {
      url: 'https://www.le39v.com/en/carte',
      language: 'en',
      traffic: 0.09, // 0.45%
      keywords: 1,
      mainKeyword: 'le 39v menu',
      conversionRate: 7.2
    },
    {
      url: 'https://www.le39v.com/en/content/the-venue',
      language: 'en',
      traffic: 0.09, // 0.45%
      keywords: 12,
      mainKeyword: 'le 39v paris',
      conversionRate: 6.8
    },
    {
      url: 'https://www.le39v.com/fr/hospitalite',
      language: 'fr',
      traffic: 0.002, // <0.01%
      keywords: 1,
      mainKeyword: 'hospitalité 39v',
      conversionRate: 5.5
    }
  ] as InternationalPage[],

  topKeywords: {
    en: [
      {
        id: 'kw_en_1',
        language: 'en',
        keyword: 'le 39v',
        volume: 140,
        position: 4,
        intent: 'navigational',
        targetUrl: '/en',
        opportunity: 'high'
      },
      {
        id: 'kw_en_2',
        language: 'en',
        keyword: '39v',
        volume: 40,
        position: 4,
        intent: 'navigational',
        targetUrl: '/en',
        opportunity: 'medium'
      }
    ],
    es: [
      {
        id: 'kw_es_1',
        language: 'es',
        keyword: 'le 39v paris',
        volume: 20,
        position: 15,
        intent: 'navigational',
        targetUrl: '/es',
        opportunity: 'medium'
      }
    ],
    pt: [
      {
        id: 'kw_pt_1',
        language: 'pt',
        keyword: 'le 39v',
        volume: 10,
        position: 12,
        intent: 'navigational',
        targetUrl: '/pt',
        opportunity: 'low'
      }
    ]
  } as Record<string, InternationalKeyword[]>
};

// Données internationales Café Max (basé sur SEMrush)
export const internationalCafeMax = {
  traffic: {
    fr: {
      language: 'fr' as const,
      country: 'France',
      traffic: 10, // 69% de 15
      percentage: 69,
      keywords: 48,
      avgPosition: 32.5
    },
    en: {
      language: 'en' as const,
      country: 'International',
      traffic: 2, // 15% de 15
      percentage: 15,
      keywords: 7,
      avgPosition: 39.4 // Version EN mal positionnée
    },
    us: {
      language: 'en' as const,
      country: 'États-Unis',
      traffic: 1.2, // 8% de 15
      percentage: 8,
      keywords: 4,
      avgPosition: 42.0
    },
    uk: {
      language: 'en' as const,
      country: 'Royaume-Uni',
      traffic: 0.8, // 5% de 15
      percentage: 5,
      keywords: 2,
      avgPosition: 45.0
    }
  } as Record<string, InternationalTraffic>,

  brandMetrics: {
    branded: { 
      keywords: 8, // café max, max cafes, etc.
      traffic: 12, 
      percentage: 80 
    },
    nonBranded: { 
      keywords: 71, // Requêtes génériques
      traffic: 3, 
      percentage: 20 // Très faible !
    }
  } as BrandMetrics,

  searchIntents: [
    {
      type: 'navigational' as const,
      volume: 2210,
      percentage: 75, // Principalement recherches de marque
      keywords: 8,
      traffic: 11.3,
      examples: ['café max', 'cafe max invalides', 'max cafes']
    },
    {
      type: 'commercial' as const,
      volume: 8400,
      percentage: 15,
      keywords: 32,
      traffic: 2.2,
      examples: ['bistrot invalides', 'restaurant 7ème', 'déjeuner invalides']
    },
    {
      type: 'informational' as const,
      volume: 1800,
      percentage: 8,
      keywords: 6,
      traffic: 1.2,
      examples: ['restaurant historique paris', 'bistrot résistance']
    },
    {
      type: 'transactional' as const,
      volume: 680,
      percentage: 2,
      keywords: 4,
      traffic: 0.3,
      examples: ['réserver café max', 'menu café max']
    }
  ] as SearchIntent[],

  topPages: [
    {
      url: 'https://www.cafemax.fr/',
      language: 'fr' as const,
      traffic: 14.75,
      keywords: 52,
      mainKeyword: 'café max',
      conversionRate: 2.8
    },
    {
      url: 'https://www.cafemax.fr/en',
      language: 'en' as const,
      traffic: 1.61,
      keywords: 23,
      mainKeyword: 'traditional french restaurant paris',
      conversionRate: 1.2
    },
    {
      url: 'https://www.cafemax.fr/assets/pdf/CARTE-DES-METS.pdf',
      language: 'fr' as const,
      traffic: 0.87,
      keywords: 7,
      mainKeyword: 'menu café max',
      conversionRate: 0.5
    },
    {
      url: 'https://www.cafemax.fr/fr/reserver',
      language: 'fr' as const,
      traffic: 0.68,
      keywords: 7,
      mainKeyword: 'réserver café max',
      conversionRate: 4.2
    }
  ] as InternationalPage[],

  topKeywords: {
    en: [
      {
        id: 'int_cm_en_1',
        language: 'en' as const,
        keyword: 'traditional french restaurant paris',
        volume: 8900,
        position: 39, // Mal positionné !
        intent: 'commercial' as const,
        targetUrl: '/en',
        opportunity: 'high' as const
      },
      {
        id: 'int_cm_en_2',
        language: 'en' as const,
        keyword: 'bistro near eiffel tower',
        volume: 5400,
        position: 75,
        intent: 'commercial' as const,
        targetUrl: '/en',
        opportunity: 'high' as const
      },
      {
        id: 'int_cm_en_3',
        language: 'en' as const,
        keyword: 'authentic parisian bistro',
        volume: 3200,
        position: 68,
        intent: 'commercial' as const,
        targetUrl: '/en',
        opportunity: 'high' as const
      },
      {
        id: 'int_cm_en_4',
        language: 'en' as const,
        keyword: 'restaurant invalides paris',
        volume: 1800,
        position: 82,
        intent: 'commercial' as const,
        targetUrl: '/en',
        opportunity: 'medium' as const
      }
    ],
    es: [], // Pas de version ES
    pt: []  // Pas de version PT
  } as Record<string, InternationalKeyword[]>
};

export const getInternationalData = (restaurantId: string) => {
  return restaurantId === '39v' ? international39V : internationalCafeMax;
};